import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { contactKeys } from '@/lib/queryKeys';

export type ContactFinancialActivityEntry =
  | {
      type: 'invoice';
      occurredAt: string;
      invoiceId: string;
      invoiceNumber: string;
      status: string;
      amount: number;
      balanceDue: number;
    }
  | {
      type: 'payment';
      occurredAt: string;
      paymentId: string;
      invoiceId?: string | null;
      status: string;
      amount: number;
      paymentMethod?: string;
      /** How the payment came in; `CHEQUE_SCAN` for a check scanned through Payabli. */
      flow?: string | null;
      gatewayProvider?: string | null;
      gatewayTxnId?: string | null;
      updatedBy?: string | null;
      transactionId?: string | null;
      unappliedAmount: number;
      /** Set on a bank return's offsetting row: the payment the bank took back. */
      reversalOf?: string | null;
      /** Set when staff cancelled this bank payment before it cleared; it reads "Cancelled". */
      cancelledByStaffAt?: string | null;
    }
  | {
      type: 'refund';
      occurredAt: string;
      refundId: string;
      paymentId: string;
      creditMemoId?: string | null;
      status: string;
      amount: number;
      reason?: string | null;
    }
  | {
      type: 'credit_memo';
      occurredAt: string;
      creditMemoId: string;
      creditMemoNumber: string;
      invoiceId?: string | null;
      creditMemoType: string;
      status: string;
      amount: number;
      remainingCredit: number;
    };

interface ContactFinancialActivityResponse {
  contactId: string;
  activity: ContactFinancialActivityEntry[];
}

export function useContactFinancialActivity(contactId: string, limit = 50) {
  return useApiQuery<ContactFinancialActivityResponse>({
    queryKey: contactKeys.financialActivity(contactId, limit),
    queryFn: () =>
      apiGet<ContactFinancialActivityResponse>(
        operationsClient,
        `/payments/activity/contact/${contactId}`,
        { params: { limit } },
      ),
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}
